import React from 'react';
import { List, Text, Badge, Group, Paper, Stack, Title } from '@mantine/core';
import {
    IconFlame,
    IconTarget,
    IconSword,
    IconBomb,
    IconAward,
} from '@tabler/icons-react';
import { Highlight } from 'types/analytics';

interface HighlightsListProps {
    highlights: Highlight[];
    limit?: number;
}

const getHighlightIcon = (type: Highlight['type']) => {
    switch (type) {
        case 'ace':
            return <IconAward size={16} />;
        case 'clutch':
            return <IconFlame size={16} />;
        case 'ninja_defuse':
            return <IconBomb size={16} />;
        case 'quad_kill':
        case 'triple_kill':
        case 'double_kill':
            return <IconSword size={16} />;
        default:
            return <IconTarget size={16} />;
    }
};

const getHighlightColor = (type: Highlight['type']) => {
    switch (type) {
        case 'ace':
            return 'yellow';
        case 'clutch':
            return 'red';
        case 'ninja_defuse':
            return 'green';
        case 'quad_kill':
            return 'orange';
        case 'triple_kill':
            return 'blue';
        case 'double_kill':
            return 'gray';
        default:
            return 'violet';
    }
};

const getHighlightLabel = (highlight: Highlight): string => {
    switch (highlight.type) {
        case 'ace':
            return 'ACE';
        case 'clutch':
            return `Clutch ${highlight.situation || '1vX'}`;
        case 'ninja_defuse':
            return 'Ninja Defuse';
        case 'quad_kill':
            return 'Quad Kill';
        case 'triple_kill':
            return 'Triple Kill';
        case 'double_kill':
            return 'Double Kill';
        default:
            return highlight.type;
    }
};

export const HighlightsList: React.FC<HighlightsListProps> = ({ highlights, limit }) => {
    const displayedHighlights = limit ? highlights.slice(0, limit) : highlights;

    if (displayedHighlights.length === 0) {
        return (
            <Paper shadow="sm" padding="md" radius="md" withBorder>
                <Text c="dimmed" ta="center">Ei korostuksia saatavilla</Text>
            </Paper>
        );
    }

    return (
        <Stack gap="xs">
            {displayedHighlights.map((highlight, index) => (
                <Paper key={`${highlight.steamId}-${highlight.roundNumber}-${index}`} shadow="xs" padding="sm" radius="md" withBorder>
                    <Group justify="space-between" wrap="nowrap">
                        <Group gap="xs">
                            {getHighlightIcon(highlight.type)}
                            <Text fw={500}>{highlight.playerName}</Text>
                            <Badge color={getHighlightColor(highlight.type)} variant="light" size="sm">
                                {getHighlightLabel(highlight)}
                            </Badge>
                        </Group>
                        <Text size="sm" c="dimmed">R{highlight.roundNumber}</Text>
                    </Group>
                    {highlight.type === 'clutch' && highlight.killsInClutch && (
                        <Text size="xs" c="dimmed" mt={4}>
                            {highlight.killsInClutch} tappoa
                        </Text>
                    )}
                    {highlight.type === 'ace' && highlight.kills && (
                        <Text size="xs" c="dimmed" mt={4}>
                            {highlight.kills} tappoa
                        </Text>
                    )}
                    {highlight.type === 'ninja_defuse' && highlight.terroristsAlive !== undefined && (
                        <Text size="xs" c="dimmed" mt={4}>
                            {highlight.terroristsAlive} terroristia elossa
                        </Text>
                    )}
                </Paper>
            ))}
        </Stack>
    );
};

